import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';
import { ReactComponent as MailIcon } from '@blink/assets/src/icons/mail.svg';

import { Member } from '@/shared/api/organization';
import { Action, useModal, useOrganizationId, useSendInvites } from '@/features/Modals';
import { Button, ButtonVariant } from '@/shared/ui';

enum ResendSelectedFields {
    INVITES = 'invites',
}

export const ResendSelectedModal: FC = () => {
    const { modalProps } = useModal();
    const { invites: initialInvites = [] }: { invites?: Member[] } = modalProps || {};
    const id = useOrganizationId();
    const { sendInvites, handleCloseModal } = useSendInvites(Action.RESEND);

    const [invites, setInvites] = useState<Member[]>(initialInvites);

    const { setValue } = useForm({
        mode: 'onChange',
        defaultValues: {
            invites: initialInvites,
        },
    });

    const handleCancel = () => {
        handleCloseModal();
    };

    const handleRemoveEmail = (email: string) => {
        const updatedInvites = invites.filter((invite) => invite.email !== email);
        setInvites(updatedInvites);
        setValue(ResendSelectedFields.INVITES, updatedInvites);
    };

    const handleSendInvites = async () => {
        const filteredInvites = invites.map(({ email, name }) => ({ email, name }));
        const description = `Successfully recent ${filteredInvites.length} invite(s)`;
        sendInvites({
            id,
            invites: filteredInvites,
            description,
        });
    };

    return (
        <div className='flex flex-col min-w-[500px] mt-5'>
            {invites.length > 0 && (
                <div className='flex flex-col items-start p-5 mt-5 gap-3 flex-[1_0_0] self-stretch rounded-[var(--radius-overall)] border border-opaque bg-background-primary'>
                    <div className="overflow-hidden text-content-primary [font-feature-settings:'calt_off'] text-ellipsis text-[14px] font-medium leading-[20px] tracking-[-0.42px] whitespace-nowrap">
                        Invites to be sent ({invites.length})
                    </div>
                    {invites.map((invite) => (
                        <div
                            key={invite.email}
                            className='flex justify-between items-center self-stretch px-[6px] py-3 pl-5 rounded-[var(--radius-overall)] bg-background-secondary'
                        >
                            <div className='flex items-center gap-4 [&>span]:text-content-secondary'>
                                <MailIcon />
                                <div className='flex items-center gap-4 [&>span]:text-content-secondary'>
                                    {invite.email} <span>(Member)</span>
                                </div>
                            </div>
                            <Button
                                variant={ButtonVariant.Tertiary}
                                className='flex items-center justify-center px-4 py-[10px] gap-2 text-md bg-background-secondary'
                                onClick={() => handleRemoveEmail(invite.email)}
                            >
                                <CloseIcon />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <div className='flex justify-end self-stretch gap-3 mt-7'>
                <Button
                    variant={ButtonVariant.Secondary}
                    className='flex items-center justify-center px-4 py-[10px] text-md'
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button
                    variant={ButtonVariant.Primary}
                    className='flex items-center justify-center px-4 py-[10px] gap-2 text-md'
                    disabled={invites.length === 0}
                    onClick={handleSendInvites}
                >
                    Resend Invites
                </Button>
            </div>
        </div>
    );
};
